import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { CheckCircle, Confetti, Flag, Wallet } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { applyMask } from "shared/utils/masks";
import {
  InvestmentItem,
  InvestmentItemStatus,
} from "../types/investment";

interface CompleteInvestmentLineModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: InvestmentItem | null;
  onSuccess?: () => void;
}

/**
 * Two-stage modal for closing an investment line:
 *  1. Confirm step — shows the unspent remainder that will be released
 *     back to the bucket and asks for an optional reason.
 *  2. Success step — celebratory confirmation showing the released amount
 *     and which bucket benefited from it.
 *
 * Hits POST `/investment/items/:id/status` (the audited endpoint) so the
 * BE writes both the InvestmentItemAuditLog row and the bucket RELEASE
 * transaction in a single, idempotent transition.
 */
export const CompleteInvestmentLineModal = ({
  isOpen,
  onClose,
  item,
  onSuccess,
}: CompleteInvestmentLineModalProps) => {
  const { session } = useSession();

  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState<"confirm" | "success">("confirm");

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setSubmitting(false);
      setStage("confirm");
    }
  }, [isOpen]);

  if (!item) return null;

  const estimated = Number(item.estimatedCostExclVat) || 0;
  const allocated = Number(item.allocatedBudget) || 0;
  // Releasable can technically go negative if the line was over-allocated
  // beyond its estimate; clamp to 0 since the BE only RELEASEs positive
  // remainders.
  const releaseable = Math.max(0, estimated - allocated);
  const bucketName = item.bucket?.name ?? null;

  const handleConfirm = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/investment/items/${item.id}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            newStatus: InvestmentItemStatus.CLOSED,
            reason:
              reason.trim() ||
              "Investeringslijn afgesloten — niet-bestede budget vrijgegeven",
          }),
        },
        session,
      );

      if (!response.ok) {
        let message = "Afsluiten mislukt";
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setStage("success");
      onSuccess?.();
    } catch (error: any) {
      console.error("[CompleteInvestmentLineModal] error:", error);
      toast.error(error?.message || "Kon investeringslijn niet afsluiten");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        {stage === "confirm" ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                  <Flag
                    weight="fill"
                    className="h-5 w-5 text-emerald-700"
                  />
                </div>
                <div>
                  <DialogTitle>Investeringslijn afsluiten</DialogTitle>
                  <DialogDescription>
                    Markeer "{item.title}" als afgerond.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-800">
                    Vrij te geven naar{" "}
                    {bucketName
                      ? `"${bucketName}"`
                      : "het strategisch budget"}
                  </span>
                  <span className="text-base font-semibold text-emerald-900">
                    {applyMask(releaseable, "valuta")}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-emerald-700">
                  <span>
                    Geschat {applyMask(estimated, "valuta")} − opgenomen{" "}
                    {applyMask(allocated, "valuta")}
                  </span>
                  {bucketName ? (
                    <span className="flex items-center gap-1">
                      <Wallet weight="duotone" className="h-3 w-3" />
                      {bucketName}
                    </span>
                  ) : (
                    <span>geen strategisch budget — overflow</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="complete-reason">Reden (optioneel)</Label>
                <Textarea
                  id="complete-reason"
                  placeholder="Bv. Werken volledig uitgevoerd door aannemer X"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={submitting}
              >
                Annuleren
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Flag className="mr-1 h-4 w-4" weight="fill" />
                {submitting ? "Afsluiten..." : "Afsluiten"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <div className="relative flex h-16 w-16 items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-emerald-100" />
                  <Confetti
                    weight="fill"
                    className="absolute -top-2 -right-2 h-6 w-6 text-emerald-500"
                  />
                  <Confetti
                    weight="fill"
                    className="absolute -bottom-1 -left-2 h-5 w-5 text-amber-400"
                  />
                  <CheckCircle
                    weight="fill"
                    className="relative h-10 w-10 text-emerald-600"
                  />
                </div>
                <div>
                  <DialogTitle className="text-center">
                    Investeringslijn afgesloten
                  </DialogTitle>
                  <DialogDescription className="text-center">
                    "{item.title}" is afgerond.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-emerald-700">
                Vrijgegeven naar
                {bucketName ? ` "${bucketName}"` : " het strategisch budget"}
              </p>
              <p className="mt-1 text-2xl font-semibold text-emerald-900">
                {applyMask(releaseable, "valuta")}
              </p>
              <p className="mt-1 text-[11px] text-emerald-700">
                {releaseable > 0
                  ? "Het ongebruikte deel is teruggevloeid en kan opnieuw verdeeld worden."
                  : "Het volledige geschatte budget werd opgenomen — niets om vrij te geven."}
              </p>
            </div>

            <DialogFooter>
              <Button
                onClick={onClose}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Sluiten
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
